<div class="d-flex d-md-flex justify-content-between align-items-center m-b-10">
  <fui-heading [text]="'content_packages.product_downloads' | translate" [level]="5"></fui-heading>
  <button class="fa-button m-l-10" placement="bottom" [ngbTooltip]="'content_packages.create_new_content_package' | translate" (click)="createContentPackage()">
    <fa-icon icon="plus" class="action create-action circle-icon-btn"></fa-icon>
  </button>
</div>
<div class="row">
  <div class="col-md-12">
    <!-- Loading indicator -->
    <myp-inline-loader [loading]="contentLoading" height="400px" spinnerName="loading">
      @if (contentLoading) {
        <div class="text-center py-4">
          <fa-icon icon="spinner" [animation]="'spin'" class="text-primary" style="font-size: 2rem"></fa-icon>
          <h5 class="mt-3 text-muted">{{ "content_packages.loading_content_packages" | translate }}</h5>
          <p class="text-muted">{{ "content_packages.loading_large_datasets" | translate }}</p>
        </div>
      }
    </myp-inline-loader>

    <!-- Filter should remain visible even when there are no results -->
    @if (!contentLoading) {
      <div class="m-t-20">
        <!-- Advanced Filters Section -->
        <div class="d-flex flex-wrap m-b-20 learn-content-filters content-sort" [ngStyle]="{ opacity: filters.query ? '.5' : '1' }">
          @for (config of filterConfigs; track config) {
            <div class="form-group m-r-10">
              <small class="text-muted">{{ config.label }}</small>
              <select
                [disabled]="config.disabled"
                [class.disabled]="config.disabled"
                [class.filter-selected]="config.filterSelected"
                class="form-control custom-select-sm"
                (change)="onFilterChange(config, $any($event.target).value)"
                [(ngModel)]="filters[config.key]"
              >
                @for (option of config.options; track option) {
                  <option [value]="option.value" [disabled]="option.disabled">
                    {{ option.label }}
                  </option>
                }
              </select>
            </div>
          }

          <fieldset class="sort-option sort-by two-option m-r-10">
            <legend class="sr-only">Sort by</legend>
            @for (option of sortByConfig?.options; track option) {
              <label class="radio-label">
                <input type="radio" name="sortBy" [checked]="filters.sortBy == option.value" (change)="onSortChange(sortByConfig!, option.value)" [attr.aria-label]="'Sort by ' + option.label" />
                <span [class.selected]="filters.sortBy == option.value">{{ option.label }}</span>
              </label>
            }
          </fieldset>

          <fieldset class="sort-option sort-direction two-option m-r-10">
            <legend class="sr-only">Sort direction</legend>
            @for (option of sortDirectionConfig?.options; track option) {
              <label class="radio-label">
                <input
                  type="radio"
                  name="sortDirection"
                  [checked]="filters.sortDirection == option.value"
                  (change)="onSortChange(sortDirectionConfig!, option.value)"
                  [attr.aria-label]="'Sort ' + option.label"
                />
                <span [class.selected]="filters.sortDirection == option.value">
                  <fa-icon [icon]="option.icon"></fa-icon>
                </span>
              </label>
            }
          </fieldset>

          <div class="form-group m-r-10">
            <small class="text-muted d-block">&nbsp;</small>
            <span class="btn btn-primary btn-sm" (click)="resetFilters()">{{ "general.reset_filters" | translate }}</span>
          </div>
        </div>

        <!-- Search Input Section -->
        <div class="filter-input">
          <label for="filterSearch" class="sr-only">Search...</label>
          <div class="search" aria-hidden="true"><fa-icon icon="search"></fa-icon></div>
          <input
            id="filterSearch"
            type="text"
            (keyup)="filterList($event)"
            [(ngModel)]="filters.query"
            [attr.aria-label]="'general.search' | translate"
            [placeholder]="'content_packages.search_content_packages' | translate"
          />
          @if (filters.query) {
            <button type="button" class="clear" (click)="clear()" [attr.aria-label]="'general.clear_search' | translate">
              <fa-icon icon="times"></fa-icon>
            </button>
          }
        </div>
      </div>
    }

    <!-- Content Packages List -->
    @if (!contentLoading && displayedContentPackages && displayedContentPackages.length) {
      <div class="m-t-20">
        @for (content of displayedContentPackages; track content.content_package_ids?.[0] || content.stringId || content.title) {
          <div class="m-b-10">
            <myp-list-item
              [title]="content.title"
              [metadata]="content.displayMetadata"
              [showImage]="false"
              [imageRound]="false"
              [includeChevron]="true"
              [chevronTooltip]="'content_packages.view_content_package' | translate"
              (itemClicked)="editContentPackage(content)"
            >
              <div class="button-content">
                <button
                  type="button"
                  class="circle-icon-btn m-r-10 bg-transparent border-0 p-0"
                  placement="left"
                  [ngbTooltip]="'general.edit' | translate"
                  (click)="editContentPackage(content); $event.stopPropagation()"
                >
                  <fa-icon icon="edit" class="action" aria-hidden="true"></fa-icon>
                  <span class="sr-only">{{ "general.edit" | translate }}</span>
                </button>
                <button
                  type="button"
                  class="circle-icon-btn bg-transparent border-0 p-0"
                  placement="left"
                  [ngbTooltip]="'general.delete' | translate"
                  (click)="deleteContentPackage(content); $event.stopPropagation()"
                >
                  <fa-icon icon="trash" class="action text-danger" aria-hidden="true"></fa-icon>
                  <span class="sr-only">{{ "general.delete" | translate }}</span>
                </button>
              </div>
            </myp-list-item>
          </div>
        }

        <!-- Pagination -->
        @if (totalPages > 0 && displayedContentPackages.length) {
          <div class="d-flex justify-content-center m-b-20 m-t-20">
            <div class="d-flex align-items-center">
              <div style="width: 30px">
                @if (page > 0) {
                  <button class="fa-button focus" (click)="paginate('previous')">
                    <fa-icon class="circle-icon-btn" icon="chevron-left"></fa-icon>
                    <span class="sr-only">{{ "general.previous" | translate }}</span>
                  </button>
                }
              </div>
              <small class="d-block m-x-10">{{ page * limit + 1 }} - {{ page * limit + limit > totalItems ? totalItems : page * limit + limit }} of {{ totalItems }}</small>
              <div style="width: 30px">
                @if (page < totalPages) {
                  <button class="fa-button focus" (click)="paginate('next')">
                    <fa-icon class="circle-icon-btn" icon="chevron-right"></fa-icon>
                    <span class="sr-only">{{ "general.next" | translate }}</span>
                  </button>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Empty state -->
    @if (!contentLoading && (!displayedContentPackages || displayedContentPackages.length === 0)) {
      <div class="text-center py-5">
        <fa-icon icon="file" class="text-muted" style="font-size: 3rem"></fa-icon>
        @if (filters.query) {
          <h4 class="mt-3 text-muted">{{ "content_packages.no_results_for" | translate }} "{{ filters.query }}"</h4>
          <p class="text-muted">{{ "content_packages.try_adjusting_search" | translate }}</p>
          <button class="btn btn-outline-secondary" (click)="clear()">
            <fa-icon icon="times"></fa-icon>
            {{ "general.clear_search" | translate }}
          </button>
        } @else {
          <h4 class="mt-3 text-muted">{{ "content_packages.no_content_packages_found" | translate }}</h4>
          <p class="text-muted">{{ "content_packages.create_first_content_package" | translate }}</p>
          <button class="btn btn-primary" (click)="createContentPackage()">
            <fa-icon icon="plus"></fa-icon>
            {{ "content_packages.create_content_package" | translate }}
          </button>
        }
      </div>
    }
  </div>
</div>
